import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, CreditCard, Cpu, BarChart3, UserCog, Settings, Power, Truck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

// Roundcube-style icon rail: a narrow dark column where every entry is a
// vertically-stacked icon + short label. Active state is a lighter navy pill.
const NAV = [
  { to: "/dashboard",      label: "Dashboard",   icon: LayoutDashboard, tid: "sidebar-nav-dashboard" },
  { to: "/households",     label: "Households",  icon: Users,           tid: "sidebar-nav-households" },
  { to: "/payments",       label: "Payments",    icon: CreditCard,      tid: "sidebar-nav-payments" },
  { to: "/supply",         label: "Supply",      icon: Truck,           tid: "sidebar-nav-supply" },
  { to: "/meters",         label: "Meters",      icon: Cpu,             tid: "sidebar-nav-meters" },
  { to: "/analytics",      label: "Analytics",   icon: BarChart3,       tid: "sidebar-nav-analytics" },
  { to: "/users",          label: "Users",       icon: UserCog,         tid: "sidebar-nav-users" },
  { to: "/configurations", label: "Config",      icon: Settings,        tid: "sidebar-nav-configurations" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-shell">
      {/* Rail sidebar — narrow icon-first navigation, inspired by Roundcube.
         `sticky h-screen` keeps the Logout button visible even on very long
         content pages so signing out never requires scrolling. */}
      <aside
        data-testid="sidebar"
        className="w-20 shrink-0 flex flex-col text-white shadow-lg sticky top-0 h-screen"
        style={{ background: "#0a1628" }}
      >
        {/* Brand circle */}
        <div className="pt-5 pb-4 flex justify-center border-b border-white/10">
          <div
            className="h-11 w-11 rounded-full bg-white/10 grid place-items-center overflow-hidden ring-2 ring-white/20"
            data-testid="sidebar-brand-logo"
            title={user?.name || "Gasify"}
          >
            <img src="/gasify_icon.png" alt="Gasify" className="h-7 w-7" style={{ filter: "brightness(0) invert(1)" }} />
          </div>
        </div>

        {/* Primary nav — icon over label */}
        <nav className="flex-1 py-3 overflow-y-auto">
          {NAV.map(({ to, label, icon: Icon, tid }) => (
            <NavLink
              key={to}
              to={to}
              data-testid={tid}
              className={({ isActive }) =>
                `rail-item ${isActive ? "rail-item-active" : ""}`
              }
              title={label}
            >
              <Icon size={20} strokeWidth={1.8} />
              <span className="rail-label">{label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer actions */}
        <div className="py-3 border-t border-white/10">
          <button
            onClick={async () => { await logout(); nav("/login"); }}
            data-testid="sidebar-logout-btn"
            className="rail-item rail-item-logout w-full"
            title="Sign out"
          >
            <Power size={22} strokeWidth={2.2} />
            <span className="rail-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30" data-testid="topbar">
          <div className="px-6 h-14 flex items-center justify-between">
            <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Operations Console</div>
            {user && (
              <div className="text-xs text-slate-500 tabular-nums" data-testid="sidebar-user-phone">
                Signed in as <span className="text-slate-900 font-semibold">{user.name || `+${user.phone}`}</span>
              </div>
            )}
          </div>
        </header>
        <main className="flex-1 p-6 overflow-x-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
